import { FunctionComponent, useCallback, useEffect, useRef } from 'react'

import { useQueryClient } from '@tanstack/react-query'
import debounce from 'lodash.debounce'

import useStandaloneViewportType from '@hooks/useStandaloneViewportType'
import { DEBOUNCE_TIMEOUT, getViewportType } from '@hooks/useViewport/utils'

const ViewportManager: FunctionComponent = () => {
  const queryClient = useQueryClient()
  const windowDimensionsFirstRun = useRef(false)
  const orientationMatchFirstRun = useRef(false)
  const orientationMatch = useRef<MediaQueryList | null>(null)
  const standaloneViewportType = useStandaloneViewportType()

  const onSizeChange = useCallback(() => {
    const calculatedSize = {
      width: window.innerWidth,
      height: window.innerHeight,
    }

    queryClient.setQueryData(
      ['viewportType'],
      standaloneViewportType ?? getViewportType(calculatedSize.width),
    )
    queryClient.setQueryData(
      ['clientViewportType'],
      standaloneViewportType ?? getViewportType(calculatedSize.width),
    )
    queryClient.setQueryData(['viewportDimensions'], calculatedSize)
    queryClient.invalidateQueries({ queryKey: ['viewportType'] })
    queryClient.invalidateQueries({ queryKey: ['clientViewportType'] })
    queryClient.invalidateQueries({ queryKey: ['viewportDimensions'] })
  }, [queryClient, standaloneViewportType])

  const onOrientationChange = useCallback(
    (e: any) => {
      queryClient.setQueryData(
        ['viewportOrientation'],
        e.matches ? 'landscape' : 'portrait',
      )
      queryClient.invalidateQueries({ queryKey: ['viewportOrientation'] })
    },
    [queryClient],
  )
  useEffect(() => {
    if (!windowDimensionsFirstRun.current) {
      onSizeChange()
      windowDimensionsFirstRun.current = true
    }
    if (!orientationMatchFirstRun.current) {
      orientationMatchFirstRun.current = true
      orientationMatch.current = matchMedia('(orientation: landscape)')
      onOrientationChange(orientationMatch.current)
    }
    const debouncedSizeChangeCallback = debounce(onSizeChange, DEBOUNCE_TIMEOUT)
    window.addEventListener('resize', debouncedSizeChangeCallback)
    orientationMatch.current?.addListener(onOrientationChange)
    return () => {
      window.removeEventListener('resize', debouncedSizeChangeCallback)
      orientationMatch.current?.removeListener(onOrientationChange)
    }
  }, [queryClient, onSizeChange, onOrientationChange])

  return null
}

export default ViewportManager
